import { useFragment } from "@/generated";
import { GetAllVocabCardsDocument, VocabCardFieldsFragmentDoc } from "@/generated/graphql";
import { ITEM_LIMIT } from "@/lib/constants/shared";
import { useQuery } from "@apollo/client/react";

const DEFAULT_LIMIT = ITEM_LIMIT.generalItemLimit;

interface UseVocabCardsOptions {
  searchText?: string | null;
  limit?: number;
  skip?: boolean;
}

/**
 * Fetches and paginates vocabulary cards.
 *
 *
 * @example
 * const { vocabCards, hasMore, loading, loadMore } = useVocabCards({
 *   searchText: "together"
 * });
 */
export function useVocabCards({
  searchText,
  limit = DEFAULT_LIMIT,
  skip = false,
}: UseVocabCardsOptions = {}) {
  const { data, loading, error, refetch, fetchMore } = useQuery(GetAllVocabCardsDocument, {
    variables: {
      limit,
      offset: 0,
      searchText: searchText || null,
    },
    skip,
    fetchPolicy: "cache-first",
  });

  const maskedItems = data?.getAllVocabCards?.items ?? [];
  const vocabCards = maskedItems.map((item) => useFragment(VocabCardFieldsFragmentDoc, item));
  const hasMore = data?.getAllVocabCards?.hasMore ?? false;
  const total = data?.getAllVocabCards?.total ?? 0;

  const loadMore = () => {
    fetchMore({
      variables: {
        limit,
        offset: maskedItems.length,
        searchText: searchText || null,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getAllVocabCards) return prev;

        return {
          getAllVocabCards: {
            ...fetchMoreResult.getAllVocabCards,
            items: [
              ...(prev.getAllVocabCards?.items ?? []),
              ...fetchMoreResult.getAllVocabCards.items,
            ],
          },
        };
      },
    });
  };

  return {
    vocabCards,
    hasMore,
    loading,
    error,
    refetch,
    loadMore,
    total,
  };
}
